
import { Collapsible } from "@/components/ui/collapsible";
import { PartFilter } from "@/data/parts";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";
import { useCollapsible } from "./collapsible-context/CollapsibleContext";
import RequirementsCollapsibleContent from "./collapsible-content/RequirementsCollapsibleContent";
import MetricsContent from "./metrics-content/MetricsContent";

type SortOption = "none" | "rating-high" | "rating-low";

interface PartCardMetricsProps {
  rating: number;
  subcategory: string;
  sortBy: SortOption;
  onRatingClick?: () => void;
  requiresRoot: boolean;
  requiresWiring?: boolean;
  requiresPrinting?: boolean;
  requiresTools?: boolean;
  requiresPurchase?: boolean;
  printedParts?: string;
  hasShoppingLinks: boolean;
  filters: PartFilter[];
  onFilterToggle?: (filter: PartFilter) => void;
  activeFilters?: PartFilter[];
}

const PartCardMetrics = ({
  rating,
  subcategory,
  sortBy,
  onRatingClick,
  requiresRoot,
  requiresWiring = false,
  requiresPrinting = false,
  requiresTools = false,
  requiresPurchase = false,
  printedParts,
  hasShoppingLinks,
  filters,
  onFilterToggle,
  activeFilters = []
}: PartCardMetricsProps) => {
  // Use the shared collapsible state
  const { isOpen, toggleOpen } = useCollapsible();
  const isMobile = useIsMobile();
  
  // State to track window resizing for responsive adjustments
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );
  
  // Effect to handle window resizing with throttling
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const throttleTime = 100; // milliseconds
    
    const handleResize = () => {
      if (timeoutId === null) {
        timeoutId = setTimeout(() => {
          setWindowWidth(window.innerWidth);
          setWindowHeight(window.innerHeight);
          timeoutId = null;
        }, throttleTime);
      }
    };
    
    // Add event listener with passive flag for performance
    window.addEventListener('resize', handleResize, { passive: true });
    
    // Initial check
    handleResize();
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="mb-3 w-full overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={toggleOpen} className="w-full">
        {/* Metrics content row with all icons in a flex container with responsive sizing */}
        <div className="flex items-center justify-center w-full">
          <MetricsContent
            rating={rating}
            subcategory={subcategory}
            sortBy={sortBy}
            onRatingClick={onRatingClick}
            requiresRoot={requiresRoot}
            requiresWiring={requiresWiring}
            requiresPrinting={requiresPrinting}
            requiresTools={requiresTools}
            requiresPurchase={requiresPurchase}
            printedParts={printedParts}
            hasShoppingLinks={hasShoppingLinks}
            filters={filters}
            onFilterToggle={onFilterToggle}
            activeFilters={activeFilters}
          />
        </div>
        
        {/* Collapsible requirements panel */}
        <RequirementsCollapsibleContent
          requiresRoot={requiresRoot}
          requiresWiring={requiresWiring}
          requiresPrinting={requiresPrinting}
          requiresTools={requiresTools}
          requiresPurchase={requiresPurchase}
          printedParts={printedParts}
          hasShoppingLinks={hasShoppingLinks}
          subcategory={subcategory}
          activeFilters={activeFilters}
          onFilterToggle={onFilterToggle}
        />
      </Collapsible>
    </div>
  );
};

export default PartCardMetrics;
